import type { Application, ApplicationStatus, SubcontractorMaster } from "../types";
import { effectivePipelineStatus } from "../utils/subcontractor";

type StepState = "done" | "current" | "current-mou" | "upcoming";

function hoComplete(status: ApplicationStatus): boolean {
  return (
    status === "ready_for_icici" ||
    status === "ready_for_mou" ||
    status === "icici_recorded" ||
    status === "subcontractor_linked"
  );
}

function stepStates(status: ApplicationStatus): StepState[] {
  const s1: StepState = "done";
  let s2: StepState;
  if (hoComplete(status)) s2 = "done";
  else s2 = "current";

  let s3: StepState;
  if (status === "subcontractor_linked" || status === "icici_recorded") s3 = "done";
  else if (status === "ready_for_mou") s3 = "current-mou";
  else if (status === "ready_for_icici") s3 = "current";
  else s3 = "upcoming";

  let s4: StepState;
  if (status === "subcontractor_linked") s4 = "done";
  else if (status === "icici_recorded") s4 = "current";
  else s4 = "upcoming";

  return [s1, s2, s3, s4];
}

type ProgressStep = {
  key: string;
  state: StepState;
  title: string;
};

function buildSteps(pipeStatus: ApplicationStatus, states: StepState[]): ProgressStep[] {
  const title3 =
    pipeStatus === "ready_for_mou" ? "MOU to attach" : "ICICI account & MOU";

  return [
    { key: "a", state: states[0], title: "Field intake" },
    { key: "b", state: states[1], title: "HO verification" },
    { key: "c", state: states[2], title: title3 },
    { key: "d", state: states[3], title: "Subcontractor" },
  ];
}

function lineTone(st: StepState): "complete" | "pending" {
  return st === "done" ? "complete" : "pending";
}

export function PipelineRail({
  application,
  masters,
}: {
  application: Application;
  masters: SubcontractorMaster[];
}) {
  const pipeStatus = effectivePipelineStatus(application, masters);
  const states = stepStates(pipeStatus);
  const steps = buildSteps(pipeStatus, states);

  return (
    <div className="case-progress" role="list" aria-label="Case pipeline">
      <div className="case-progress__steps">
        {steps.map((step, i) => {
          const leftDone = i > 0 && lineTone(steps[i - 1].state) === "complete";
          const rightDone = lineTone(step.state) === "complete";
          return (
            <div
              key={step.key}
              className={`case-progress__step case-progress__step--${step.state}`}
              role="listitem"
            >
              <div className="case-progress__track">
                {i > 0 ? (
                  <span
                    className={`case-progress__line case-progress__line--${leftDone ? "complete" : "pending"}`}
                    aria-hidden
                  />
                ) : (
                  <span className="case-progress__line case-progress__line--spacer" aria-hidden />
                )}
                <span className="case-progress__dot" />
                {i < steps.length - 1 ? (
                  <span
                    className={`case-progress__line case-progress__line--${rightDone ? "complete" : "pending"}`}
                    aria-hidden
                  />
                ) : (
                  <span className="case-progress__line case-progress__line--spacer" aria-hidden />
                )}
              </div>
              <div className="case-progress__text">
                <div className="case-progress__title">{step.title}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
